import * as t from "@babel/types";
import { ImportType } from "./jsxElementTypes";
import { pathRelative } from "./paths";

export const ensureImportInAST = (
  parsedAST: any,
  importName: string,
  importFile: string,
  currentFile?: string,
  importType?: ImportType,
  importedName?: string,
) => {
  let importToUse = importFile;

  if (!importFile) {
    return;
  }

  if (importToUse.startsWith("/")) {
    if (!currentFile) {
      throw new Error("Current file is required");
    }

    const currentDir = currentFile.substring(0, currentFile.lastIndexOf("/"));
    importToUse = "./" + pathRelative(currentDir, importFile);
  }

  // Check if it has already been imported or if there's an existing import for the same file
  // TODO: this fails if the name is the same as another import
  let addIndex: number | string = 0;
  let existingImportDeclaration: t.ImportDeclaration | null = null;

  for (let i = 0; i < parsedAST.program.body.length; i++) {
    const node = parsedAST.program.body[i];
    if (t.isImportDeclaration(node)) {
      // Check if this import name already exists
      for (const specifier of node.specifiers) {
        if (specifier?.local?.name === importName) {
          addIndex = "exists";
        }
      }

      // Check if there's already an import declaration for the same file
      if (node.source?.value === importToUse) {
        existingImportDeclaration = node;
      }

      // Add it after the last import
      if (addIndex !== "exists") {
        addIndex = i;
      }
    }
  }

  if (addIndex !== "exists") {
    if (importName) {
      let specifier;
      switch (importType) {
        case ImportType.ImportDefaultSpecifier:
          specifier = t.importDefaultSpecifier(t.identifier(importName));
          break;

        case ImportType.ImportSpecifier:
          specifier = t.importSpecifier(
            t.identifier(importName),
            t.identifier(importedName || importName),
          );
          break;

        case ImportType.ImportNamespaceSpecifier:
          specifier = t.importNamespaceSpecifier(t.identifier(importName));
          break;
      }

      // If there's an existing import declaration for the same file, add the specifier to it
      if (existingImportDeclaration && specifier) {
        existingImportDeclaration.specifiers.push(specifier);
      } else {
        // Otherwise, create a new import declaration
        const specifiers = [specifier];
        parsedAST.program.body.splice(
          addIndex,
          0,
          t.importDeclaration(specifiers, t.stringLiteral(importToUse)),
        );
      }
    } else {
      parsedAST.program.body.splice(
        addIndex,
        0,
        t.importDeclaration([], t.stringLiteral(importToUse)),
      );
    }
  }
};

export const convertValueToASTNode = (
  value: any,
): t.Literal | t.Identifier | t.Expression => {
  switch (typeof value) {
    case "number":
      return t.numericLiteral(value);
    case "string":
      return t.stringLiteral(value);
    case "boolean":
      return t.booleanLiteral(value);
    case "object":
      if (Array.isArray(value)) {
        return t.arrayExpression(value.map(convertValueToASTNode));
      } else if (value === null) {
        return t.nullLiteral();
      } else {
        return t.objectExpression(
          Object.keys(value).map((key) =>
            t.objectProperty(
              t.identifier(key),
              convertValueToASTNode(value[key]),
            ),
          ),
        );
      }
    case "undefined":
      return t.identifier("undefined");
    default:
      throw new Error(`Unsupported type: ${typeof value}`);
  }
};
